'use client';

import React, { useEffect, useMemo } from 'react';
import { PhoneNumberRegistrationInput } from '.';
import { telegramGeoDetectGetLocation, useTelegramGeoGetAllCountries } from '@/generated';
import { useCountryId } from '@/contexts/CookieContext';

type Props = {
  countryId?: string;
  phoneNumber?: string;
  onChange: (newCountryId: string, newPhoneNumber: string, newPrefix?: string) => void;
  shouldAutoFetchCountry?: boolean;
  ltrField?: boolean;
  autoFocus?: boolean;
};

const toMasks = (
  placeholder: string,
  inputMask: string | null,
  allowedPhoneLengths: number[],
): { mask: string; lazy: false }[] => {
  const primary: { mask: string; lazy: false } = { mask: placeholder, lazy: false };
  if (!inputMask) return [primary];

  const derivedPattern = inputMask.replace(/\d/g, '0');
  if (derivedPattern === placeholder) return [primary];

  const derivedLength = derivedPattern.replace(/\D/g, '').length;
  const isAllowed = !allowedPhoneLengths.length || allowedPhoneLengths.includes(derivedLength);
  if (!isAllowed) return [primary];

  return [primary, { mask: derivedPattern, lazy: false }];
};

export const PhoneNumberInputAdapter: React.FC<Props> = ({ shouldAutoFetchCountry = false, ...props }) => {
  const { data: countriesResponse = [] } = useTelegramGeoGetAllCountries();

  const countries = useMemo(() => {
    return countriesResponse
      .filter((c) => c.authMethods.includes('SMS'))
      .map((c) => {
        return {
          title: c.name,
          code: c.countryCode,
          prefix: c.phoneCode,
          masks: toMasks(c.phonePlaceholder, c.inputMask, c.allowedPhoneLengths),
          id: c.id,
          flagEmoji: getFlagEmoji(c.countryCode),
        };
      });
  }, [countriesResponse]);

  const localCountryId = useCountryId();

  useEffect(() => {
    const getGeo = async () => {
      if (localCountryId && countries.length) {
        const foundCountry = countries.find(({ id }) => id === localCountryId);
        if (foundCountry) {
          props.onChange(foundCountry.id, '', foundCountry.prefix);
          return;
        }
      }

      const res = await telegramGeoDetectGetLocation();
      const availableCountry = countries.find(({ id }) => id === res.country?.id);
      if (res.country && availableCountry) {
        props.onChange(res.country.id, '', res.country.phoneCode);
      }

      const israel = countries.find(({ title }) => title === 'Israel');
      if (!israel) {
        return;
      }
      props.onChange(israel.id, '', israel.prefix);
    };

    if (shouldAutoFetchCountry) {
      getGeo();
    }
  }, [localCountryId, shouldAutoFetchCountry, props.onChange, countries]);

  return <PhoneNumberRegistrationInput countries={countries} {...props} />;
};

const getFlagEmoji = (countryCode: string) => {
  return `/assets/icons/flags/${countryCode.toLowerCase()}.svg`;
};
